// SectionStepsComponent.js
import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ScrollFadeWrapper, {
  ScrollFadeSection,
} from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import Image from '../../Image/Image';

// Cloudflare R2 vector
const SECTION_STEPS_VECTOR_URL =
  'https://assets.askhippo.co.za/Vectors/SectionStepsVector.svg';

const SECTION_BOX_PADDING_TOP = 32;
const TITLE_TO_SUBTITLE_GAP = 16;
const SUBTITLE_TO_CARDS_GAP = 40;
const CARD_HEIGHT = 320;
const MIN_SECTION_HEIGHT = 528;
const FOOTER_TOP_GAP = 40;
const FOOTER_BOTTOM_PADDING = 48;
const FOOTER_ESTIMATED_HEIGHT = 48;
const NO_FOOTER_BOTTOM_GAP = 32;

const SectionStepsComponent = ({ data }) => {
  if (!data) return null;
  const { title, subtitle, steps = [], footer } = data;

  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.getBoundingClientRect().height);
    }
  }, [title, subtitle]);

  const cardsTop =
    SECTION_BOX_PADDING_TOP + headerHeight + SUBTITLE_TO_CARDS_GAP;
  const hasFooter = Boolean(String(footer || '').trim());
  const footerTop = cardsTop + CARD_HEIGHT + FOOTER_TOP_GAP;
  const sectionHeight = Math.max(
    MIN_SECTION_HEIGHT,
    cardsTop +
      CARD_HEIGHT +
      (hasFooter
        ? FOOTER_TOP_GAP + FOOTER_ESTIMATED_HEIGHT + FOOTER_BOTTOM_PADDING
        : NO_FOOTER_BOTTOM_GAP)
  );

  return (
    <ScrollFadeSection
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {/* Green box */}
        <Box
          sx={{
            position: 'relative',
            width: '1010px',
            height: `${sectionHeight}px`,
            backgroundColor: '#C7CA00',
            borderRadius: '34px',
            px: '32px',
            py: '32px',
            overflow: 'visible',
            zIndex: 1,
          }}
        >
          <Box ref={headerRef}>
            {/* Title */}
            <Typography
              component={'h3'}
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '3rem',
                lineHeight: '3.25rem',
                letterSpacing: '-0.03em',
                textAlign: 'center',
                color: '#162F3B',
              }}
            >
              {title}
            </Typography>

            {/* Description */}
            <Typography
              component={'p'}
              sx={{
                mt: `${TITLE_TO_SUBTITLE_GAP}px`,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: '1.5rem',
                lineHeight: '1.75rem',
                letterSpacing: '-0.03em',
                textAlign: 'center',
                color: '#162F3B',
                maxWidth: 780,
                mx: 'auto',
              }}
            >
              {subtitle}
            </Typography>
          </Box>

          {/* Cards row */}
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              top: `${cardsTop}px`,
              display: 'flex',
              gap: '24px',
              zIndex: 3,
            }}
          >
            {steps.map((step, i) => {
              const hasNumber =
                step.number !== undefined &&
                step.number !== null &&
                step.number !== '';

              return (
                <ScrollFadeWrapper key={i} delay={i * 0.15}>
                  <Box
                    sx={{
                      position: 'relative',
                      width: '389px',
                      height: `${CARD_HEIGHT}px`,
                      backgroundColor: '#FFFFFF',
                      borderRadius: '32px',
                      pt: '100px',
                      pr: '64px',
                      pb: '64px',
                      pl: '64px',
                      boxShadow:
                        '0px 1px 2px rgba(162,172,177,0.20), 0px 12px 24px -4px rgba(22,47,59,0.12)',
                    }}
                  >
                    {/* Badge: number by default, icon when the config omits number */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '-20px',
                        left: '32px',
                        width: '96px',
                        height: '96px',
                        zIndex: 5,
                      }}
                    >
                      <Image
                        width={96}
                        height={96}
                        src={SECTION_STEPS_VECTOR_URL}
                        alt="Steps process graphic"
                        sx={{ width: '100%', height: '100%' }}
                      />

                      {hasNumber ? (
                        <Typography
                          component={'p'}
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'grid',
                            placeItems: 'center',
                            fontFamily: 'Pally, Inter, sans-serif',
                            fontWeight: 700,
                            fontSize: '4.5rem',
                            lineHeight: '4.25rem',
                            letterSpacing: '-0.03em',
                            color: '#FFFFFF',
                          }}
                        >
                          {step.number}
                        </Typography>
                      ) : step.icon ? (
                        <Image
                          width={48}
                          height={48}
                          src={step.icon}
                          alt={step.imgAltTag || step.heading}
                          title={step.imageAltTitle || step.heading}
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '48px',
                            height: '48px',
                            objectFit: 'contain',
                            filter: 'brightness(0) invert(1)',
                          }}
                        />
                      ) : null}
                    </Box>

                    {/* Card text */}
                    <Typography
                      component={'h3'}
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '1.625rem',
                        lineHeight: '1.875rem',
                        letterSpacing: 0,
                        color: '#162F3B',
                        mb: '32px',
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {step.heading}
                    </Typography>
                    <Typography
                      component={'p'}
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        fontSize: '1rem',
                        lineHeight: '1.5rem',
                        letterSpacing: '-0.002em',
                        color: '#162F3B',
                      }}
                    >
                      {step.text}
                    </Typography>
                  </Box>
                </ScrollFadeWrapper>
              );
            })}
          </Box>

          {hasFooter && (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                top: `${footerTop}px`,
                width: '100%',
                px: '32px',
                display: 'flex',
                justifyContent: 'center',
                zIndex: 3,
              }}
            >
              <Typography
                component="p"
                sx={{
                  fontFamily: '"Body/Body/Font Family", Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: '1rem',
                  lineHeight: '1.5rem',
                  letterSpacing: '-0.002em',
                  textAlign: 'center',
                  color: '#162F3B',
                  maxWidth: 946,
                  whiteSpace: 'pre-line',
                }}
              >
                {footer}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </ScrollFadeSection>
  );
};

export default SectionStepsComponent;
